<template>
  <div class="dialog-template" :style="{ width: props.width, height: props.height }">
    <div class="dialog-template__header">
      <div class="dialog-template__header-main">
        <h2 class="dialog-template__title">{{ title }}</h2>
      </div>
      <div class="dialog-template__header-actions">
        <HelpTip v-if="props.info" class="dialog-template__info-tip">
          {{ props.info }}
        </HelpTip>
        <button
          type="button"
          class="dialog-template__close"
          aria-label="Close dialog"
          @click="emit('cancel')"
        >
          &times;
        </button>
      </div>
    </div>

    <div class="dialog-template__body dialog-form" :class="{ 'dialog-template__body--scrollable': props.bodyScrollable }">
      <p v-if="props.isConfirm" class="dialog-template__confirm-message">{{ props.message }}</p>
      <slot v-else />
    </div>

    <div class="dialog-template__footer">
      <button
        v-for="buttonLabel in normalizedFooterButtons"
        :key="buttonLabel"
        type="button"
        :class="getButtonClass(buttonLabel)"
        @click="handleFooterButtonClick(buttonLabel)"
      >
        {{ buttonLabel }}
      </button>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import HelpTip from '@/components/HelpTip.vue';

const props = defineProps({
  title: {
    type: String,
    default: '',
  },
  info: {
    type: String,
    default: '',
  },
  width: {
    type: String,
    default: '30%',
  },
  height: {
    type: String,
    default: 'auto',
  },
  bodyScrollable: {
    type: Boolean,
    default: false,
  },
  footerButtons: {
    type: Array,
    default: () => ['Cancel', 'Create'],
  },
  isConfirm: {
    type: Boolean,
    default: false,
  },
  message: {
    type: String,
    default: 'Are you sure?',
  },
});

const emit = defineEmits(['footer-click', 'cancel', 'create', 'confirm']);

const normalizedFooterButtons = computed(() => {
  if (props.isConfirm) {
    return ['No', 'Yes'];
  }

  if (!Array.isArray(props.footerButtons) || props.footerButtons.length === 0) {
    return ['Cancel', 'Create'];
  }
  return props.footerButtons;
});

function getButtonClass(buttonLabel) {
  const label = String(buttonLabel || '').toLowerCase();
  if (label === 'cancel' || label === 'close' || label === 'no') {
    return 'dialog-button dialog-button-cancel';
  }
  return 'dialog-button dialog-button-confirm';
}

function handleFooterButtonClick(buttonLabel) {
  emit('footer-click', buttonLabel);

  const label = String(buttonLabel || '').toLowerCase();
  if (label === 'cancel' || label === 'no') {
    emit('cancel');
  }
  if (label === 'create') {
    emit('create');
  }
  if (label === 'yes') {
    emit('confirm');
  }
}
</script>

<style lang="scss" scoped>
.dialog-template {
  display: flex;
  flex-direction: column;
  border-radius: 8px;
  overflow: hidden;
}

.dialog-template__header {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  gap: 24px;
  padding: 8px 12px;
  border-bottom: 1px solid grey;
  border-radius: 8px 8px 0 0;
  @include themify() {
    background: linear-gradient(45deg, themed('color-primary-600') 0%, themed('color-primary') 100%);
    color: themed('modal-header-text-color');
  }
}

.dialog-template__header-main {
  display: flex;
  align-items: center;
  gap: 0;
  flex: 1 1 auto;
  min-width: 0;
}

.dialog-template__header-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.dialog-template__title {
  margin: 0;
  padding-left: 18px;
  text-align: left;
  white-space: nowrap;
  text-overflow: ellipsis;
  min-width: 100px;
  overflow: hidden;
  flex: 1 1 fit-content;
  max-width: fit-content;
  font-size: 1.5rem !important;
  font-weight: 300;
  color: #fff;
}

.dialog-template__close {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  background: transparent;
  border: none;
  border-radius: 50%;
  color: #fff;
  font-size: 36px;
  line-height: 1;
  cursor: pointer;
}

.dialog-template__close:hover {
  background: rgba(38, 61, 160, 0.6);
}

.dialog-template__info-tip {
  flex: 0 0 auto;
}

.dialog-template__body {
  padding: 20px;
  font-family: inherit;
  font-size: 1rem;
  line-height: 1.4;
  min-height: 0;
  @include themify() {
    background: rgb(249, 249, 249);
    color: themed('body-text-color');
  }
}

.dialog-template__confirm-message {
  margin: 0;
  font-size: 1rem;
  line-height: 1.4;
}

.dialog-template__body--scrollable {
  flex: 1 1 auto;
  overflow-y: auto;
}

.dialog-template__footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 10px 20px 10px;
  border-top: 1px solid rgba(0, 0, 0, 0.06);
  background: #fff;
  border-radius: 0 0 8px 8px;
}

.settings {
  padding-top: 16px;
  padding-bottom: 16px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  margin-left: auto;
  margin-right: auto;
  @include themify() {
    background: themed('color-white-600');
  }
}

.container {
  display: flex;
  flex-direction: row;
  padding: 0 16px;
  gap: 16px;
}

.static-card {
  display: flex;
  flex-direction: column;
  position: relative;
  padding: 16px;
  text-align: left;
  border-radius: 6px;
  height: fit-content;
  flex: 1 1 auto;
  width: 100%;
  @include themify() {
    border: 1px solid themed('color-primary-200');
    background: themed('color-white-100');
  }
  box-shadow: 1px 1px 4px 1px rgba(0, 0, 0, 0.2), 0 0 1px 1px rgba(0, 0, 0, 0.05);
}

@media (max-width: $breakpoint-lg) {
  .settings {
    width: 100%;
  }

  .container {
    flex-direction: column;
    width: 100%;
  }
}

@media (min-width: $breakpoint-sm) {
  .dialog-template__body {
    min-width: 420px;
  }
}
</style>
